// frontend/src/pages/ProcessingPage.tsx

import React, { useState, useEffect } from 'react'
import api from '../api/axios'
import './Processing.css'

import bgImg       from '../assets/processing/bg.png'
import tabBtnImg   from '../assets/processing/button.png'
import furnaceImg  from '../assets/processing/furnace.png'
import sawImg      from '../assets/processing/saw.png'
import spinnerImg  from '../assets/processing/spinner.png'

type Materials = {
  iron_ore: number
  wood:     number
  cotton:   number
  [key: string]: number
}

type Tab = 'furnace' | 'saw' | 'fiber'

export default function ProcessingPage() {
  const [activeTab, setActiveTab] = useState<Tab>('furnace')
  const [materials, setMaterials] = useState<Materials>({
    iron_ore: 0,
    wood:     0,
    cotton:   0
  })
  const [amounts, setAmounts] = useState({ melt: 1, saw: 1, fiber: 1 })
  const [msg, setMsg]         = useState<string | null>(null)

  // load materials on mount
  useEffect(() => {
    api.get<{ inventory: { materials: Materials } }>('/character')
      .then(res => setMaterials(res.data.inventory.materials))
      .catch(() => setMsg('Failed to load materials.'))
  }, [])

  // generic processing handler
  async function handleProcess(endpoint: 'melt'|'saw'|'fiber') {
    setMsg(null)
    const amt = amounts[endpoint]
    if (amt <= 0) {
      setMsg('Amount must be ≥ 1.')
      return
    }
    try {
      const res = await api.post<{ message: string }>(
        `/${endpoint}`, { amount: amt }
      )
      setMsg(res.data.message || 'Done!')
      // refresh materials
      setTimeout(() => {
        api.get<{ inventory: { materials: Materials } }>('/character')
          .then(r => setMaterials(r.data.inventory.materials))
          .catch(() => {/* ignore */})
      }, 500)
    } catch (err: any) {
      const e = err.response?.data?.error || err.message
      setMsg(`Error: ${e}`)
    }
  }

  // render the appropriate form section
  function renderSection() {
    switch (activeTab) {
      case 'furnace':
        return (
          <section className="proc-section">
            <h3>Furnace<br/>Iron Ore → Iron Bar</h3>
            <p>You have: {materials.iron_ore} iron ore</p>
            <label>
              Amount:
              <input
                type="number" min={1}
                value={amounts.melt}
                onChange={e => setAmounts(a => ({ ...a, melt: Number(e.target.value) }))}
              />
            </label>
            <button
              onClick={() => handleProcess('melt')}
              disabled={materials.iron_ore < amounts.melt}
            >
              Melt
            </button>
          </section>
        )
      case 'saw':
        return (
          <section className="proc-section">
            <h3>Saw Mill<br/>Wood → Wooden Plank</h3>
            <p>You have: {materials.wood} wood</p>
            <label>
              Amount:
              <input
                type="number" min={1}
                value={amounts.saw}
                onChange={e => setAmounts(a => ({ ...a, saw: Number(e.target.value) }))}
              />
            </label>
            <button
              onClick={() => handleProcess('saw')}
              disabled={materials.wood < amounts.saw}
            >
              Saw
            </button>
          </section>
        )
      case 'fiber':
        return (
          <section className="proc-section">
            <h3>Spinner<br/>Cotton → Fiber</h3>
            <p>You have: {materials.cotton} cotton</p>
            <label>
              Amount:
              <input
                type="number" min={1}
                value={amounts.fiber}
                onChange={e => setAmounts(a => ({ ...a, fiber: Number(e.target.value) }))}
              />
            </label>
            <button
              onClick={() => handleProcess('fiber')}
              disabled={materials.cotton < amounts.fiber}
            >
              Process
            </button>
          </section>
        )
    }
  }

  // choose background image per tab
  const bgForTab = activeTab === 'furnace'
    ? furnaceImg
    : activeTab === 'saw'
      ? sawImg
      : spinnerImg

  return (
    <div
      className="processing-wrapper"
      style={{
        background: `url(${bgImg}) no-repeat center top`,
        backgroundSize: 'cover',
        minHeight: '100vh',
        padding: '2rem 1rem',
      }}
    >
      <div className="processing-page">
        <h2>Processing</h2>
        {msg && <div className="message">{msg}</div>}

        <div className="proc-tab-bar">
          {(['furnace','saw','fiber'] as Tab[]).map(tab => (
            <button
              key={tab}
              className={`proc-tab-btn${activeTab===tab ? ' active' : ''}`}
              style={{ backgroundImage: `url(${tabBtnImg})` }}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'furnace' ? 'Furnace'
               : tab === 'saw'     ? 'Saw Mill'
               :                    'Spinner'}
            </button>
          ))}
        </div>

        <div
          className="proc-section-wrapper"
          style={{
            backgroundImage: `url(${bgForTab})`
          }}
        >
          {renderSection()}
        </div>
      </div>
    </div>
  )
}
